import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { History, ArrowUpCircle, ArrowDownCircle, Trash2, RefreshCw, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SortableHeader, sortData } from '../components/SortableHeader';
import { toast } from 'sonner';

const TransactionHistory = () => {
    const { user } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortKey, setSortKey] = useState('timestamp');
    const [sortDirection, setSortDirection] = useState('desc');
    const API_URL = '';
    
    // Handle sorting
    const handleSort = (key, direction) => {
        setSortKey(key);
        setSortDirection(direction);
    };
    
    // Sort transactions
    const sortedTransactions = useMemo(() => {
        return sortData(transactions, sortKey, sortDirection);
    }, [transactions, sortKey, sortDirection]);

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/admin-transactions`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setTransactions(data);
            } else {
                toast.error('Error al cargar las transacciones');
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
            toast.error('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, []);

    const handleClearHistory = async () => {
        if (!window.confirm('¿Está seguro de que desea eliminar todo el historial de transacciones?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/admin-transactions`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                toast.success('Historial eliminado correctamente');
                setTransactions([]);
            } else {
                toast.error('Error al eliminar el historial');
            }
        } catch (error) {
            console.error('Error clearing history:', error);
            toast.error('Error de conexión');
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    return (
        <div className="space-y-6" data-testid="transaction-history-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center">
                        <History className="w-6 h-6 text-sky-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">Historial de Transacciones</h1>
                        <p className="text-slate-500 text-sm">Registro de ajustes del administrador</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={fetchTransactions}
                        className="flex items-center gap-2"
                        data-testid="refresh-btn"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Actualizar
                    </Button>
                    {user?.role === 'admin' && transactions.length > 0 && (
                        <Button
                            variant="destructive"
                            onClick={handleClearHistory}
                            className="flex items-center gap-2"
                            data-testid="clear-history-btn"
                        >
                            <Trash2 className="w-4 h-4" />
                            Limpiar Historial
                        </Button>
                    )}
                </div>
            </div>

            {/* Transactions List */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="spinner mx-auto mb-4"></div>
                        <p className="text-slate-500">Cargando transacciones...</p>
                    </div>
                ) : transactions.length === 0 ? (
                    <div className="p-8 text-center">
                        <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">No hay transacciones registradas</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="transactions-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <SortableHeader 
                                        label="Fecha" 
                                        sortKey="created_at" 
                                        currentSort={sortKey} 
                                        currentDirection={sortDirection} 
                                        onSort={handleSort}
                                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider"
                                    />
                                    <SortableHeader 
                                        label="Usuario" 
                                        sortKey="user_name" 
                                        currentSort={sortKey} 
                                        currentDirection={sortDirection} 
                                        onSort={handleSort}
                                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider"
                                    />
                                    <SortableHeader 
                                        label="Tipo" 
                                        sortKey="type" 
                                        currentSort={sortKey} 
                                        currentDirection={sortDirection} 
                                        onSort={handleSort}
                                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider"
                                    />
                                    <SortableHeader 
                                        label="Monto" 
                                        sortKey="amount" 
                                        currentSort={sortKey} 
                                        currentDirection={sortDirection} 
                                        onSort={handleSort}
                                        className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider"
                                    />
                                    <SortableHeader 
                                        label="Total Anterior" 
                                        sortKey="previous_total" 
                                        currentSort={sortKey} 
                                        currentDirection={sortDirection} 
                                        onSort={handleSort}
                                        className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider"
                                    />
                                    <SortableHeader 
                                        label="Nuevo Total" 
                                        sortKey="new_total" 
                                        currentSort={sortKey} 
                                        currentDirection={sortDirection} 
                                        onSort={handleSort}
                                        className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider"
                                    />
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                                        Nota
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {sortedTransactions.map((transaction) => (
                                    <tr 
                                        key={transaction.id} 
                                        className="hover:bg-slate-50 transition-colors"
                                        data-testid={`transaction-row-${transaction.id}`}
                                    >
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-600">
                                            {formatDate(transaction.created_at)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className="text-sm font-medium text-slate-800">
                                                {transaction.user_name}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                                                transaction.type === 'suma' 
                                                    ? 'bg-green-100 text-green-700' 
                                                    : 'bg-red-100 text-red-700'
                                            }`}>
                                                {transaction.type === 'suma' ? (
                                                    <ArrowUpCircle className="w-3.5 h-3.5" />
                                                ) : (
                                                    <ArrowDownCircle className="w-3.5 h-3.5" />
                                                )}
                                                {transaction.type === 'suma' ? 'Suma' : 'Resta'}
                                            </span>
                                        </td>
                                        <td className={`px-4 py-4 whitespace-nowrap text-sm font-semibold text-right ${
                                            transaction.type === 'suma' ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                            {transaction.type === 'suma' ? '+' : '-'}{formatAmount(transaction.amount)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-600 text-right">
                                            {formatAmount(transaction.previous_total)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-slate-800 text-right">
                                            {formatAmount(transaction.new_total)}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-slate-600 max-w-xs">
                                            {transaction.note ? (
                                                <span className="flex items-center gap-1">
                                                    <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                                    <span className="truncate" title={transaction.note}>
                                                        {transaction.note}
                                                    </span>
                                                </span>
                                            ) : (
                                                <span className="text-slate-300 italic">Sin nota</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Summary */}
            {!loading && transactions.length > 0 && (
                <div className="bg-sky-50 rounded-xl p-4 border border-sky-200">
                    <p className="text-sm text-sky-700">
                        <span className="font-semibold">{transactions.length}</span> transacciones registradas en total
                    </p>
                </div>
            )}
        </div>
    );
};

export default TransactionHistory;
